/**
 * Main archive store - manages collection data and state
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type {
  VisitorDataResponse,
  Achievement,
  TimePeriod,
  VisitorMetadataSafe,
  SpecimenResult,
  ContributionSummary,
} from '@ephemera/shared';
import { apiClient } from '../services/api';
import type { CollectionResponseWithProgress } from '../services/api';

export const useArchiveStore = defineStore('archive', () => {
  // State
  const initialized = ref(false);
  const loading = ref(true);
  const error = ref<string | null>(null);

  // Session data
  const sessionId = ref<string | null>(null);
  const isNewSession = ref(false);
  const isBot = ref(false);

  // Enhanced visitor data from visit response
  const visitorMetadata = ref<VisitorMetadataSafe | null>(null);
  const specimenResults = ref<SpecimenResult[]>([]);
  const contributionSummary = ref<ContributionSummary | null>(null);
  const visitUnlockedAchievements = ref<Achievement[]>([]);

  // Collection data
  const currentPeriod = ref<TimePeriod>('all');
  const collection = ref<CollectionResponseWithProgress | null>(null);

  // Visitor data
  const visitorData = ref<VisitorDataResponse | null>(null);

  // SSE connection
  const sseConnected = ref(false);
  const sseDegraded = ref(false);
  let eventSource: EventSource | null = null;

  // Achievement notifications queue
  const achievementNotifications = ref<Achievement[]>([]);

  // Computed
  const totalSpecimens = computed(() => {
    if (!collection.value) return 0;
    return collection.value.stats.totalSpecimens;
  });

  const totalAchievements = computed(() => {
    if (!collection.value) return 0;
    return collection.value.stats.totalAchievements;
  });

  const geographySpecimens = computed(() => {
    return collection.value?.specimens.geography || [];
  });

  const technologySpecimens = computed(() => {
    return collection.value?.specimens.technology || [];
  });

  const temporalSpecimens = computed(() => {
    return collection.value?.specimens.temporal || [];
  });

  const infrastructureSpecimens = computed(() => {
    return collection.value?.specimens.infrastructure || [];
  });

  const digitalVisitorsSpecimens = computed(() => {
    return collection.value?.specimens.digitalVisitors || [];
  });

  const achievements = computed(() => {
    return collection.value?.achievements || [];
  });

  const achievementProgress = computed(() => {
    return collection.value?.achievementProgress || [];
  });

  // Actions
  async function initialize() {
    if (initialized.value) return;

    loading.value = true;
    error.value = null;

    try {
      // Record visit and load collection in parallel
      await Promise.all([
        recordVisit(),
        loadCollection('all'),
      ]);

      // Connect to SSE for real-time updates
      connectSSE();

      initialized.value = true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to initialize archive';
      console.error('Initialization error:', err);
    } finally {
      loading.value = false;
    }
  }

  async function recordVisit() {
    try {
      const response = await apiClient.recordVisit();
      sessionId.value = response.sessionId;
      isNewSession.value = response.isNewSession;
      isBot.value = response.isBot;

      // Store enhanced visit data
      visitorMetadata.value = response.visitorMetadata;
      specimenResults.value = response.specimenResults;
      contributionSummary.value = response.contributionSummary;
      visitUnlockedAchievements.value = response.unlockedAchievements || [];
    } catch (err) {
      throw new Error('Failed to record visit');
    }
  }

  async function loadCollection(period: TimePeriod) {
    loading.value = true;
    error.value = null;

    try {
      const data = await apiClient.getCollection(period);
      collection.value = data;
      currentPeriod.value = period;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load collection';
      console.error('Collection loading error:', err);
    } finally {
      loading.value = false;
    }
  }

  async function loadVisitorData() {
    if (!sessionId.value) return;

    try {
      const data = await apiClient.getVisitorData();
      visitorData.value = data;
    } catch (err) {
      console.error('Failed to load visitor data:', err);
    }
  }

  function changePeriod(period: TimePeriod) {
    if (period !== currentPeriod.value) {
      loadCollection(period);
    }
  }

  /**
   * Connect to SSE for real-time updates
   */
  let sseRetryCount = 0;
  const SSE_MAX_RETRIES = 2;

  function connectSSE() {
    if (eventSource) {
      console.warn('SSE already connected');
      return;
    }

    try {
      eventSource = new EventSource('/api/events');

      // Handle connection
      eventSource.addEventListener('connected', (event) => {
        console.log('SSE connected:', event.data);
        sseConnected.value = true;
        sseDegraded.value = false;
        sseRetryCount = 0;
      });

      // Handle real-time updates
      eventSource.addEventListener('update', (event) => {
        try {
          const data = JSON.parse(event.data);
          handleRealtimeUpdate(data);
        } catch (error) {
          console.error('Error parsing SSE update:', error);
        }
      });

      // Handle degraded mode
      eventSource.addEventListener('degraded', (event) => {
        console.warn('SSE degraded mode:', event.data);
        sseDegraded.value = true;
      });

      // Handle errors
      eventSource.onerror = () => {
        sseConnected.value = false;

        if (eventSource) {
          eventSource.close();
          eventSource = null;
        }

        sseRetryCount++;
        if (sseRetryCount <= SSE_MAX_RETRIES) {
          setTimeout(() => connectSSE(), 5000);
        }
      };
    } catch (error) {
      console.error('Failed to connect to SSE:', error);
    }
  }

  /**
   * Handle real-time updates from SSE
   */
  function handleRealtimeUpdate(data: any) {
    // Update statistics
    if (data.stats && collection.value) {
      collection.value.stats = {
        ...collection.value.stats,
        ...data.stats,
      };
    }

    // Show achievement notifications
    if (data.achievements && data.achievements.length > 0) {
      for (const achievement of data.achievements) {
        showAchievementNotification(achievement);
      }

      // Reload collection to show new achievements
      loadCollection(currentPeriod.value);
    }

    // Reload collection if new specimens were added
    if (data.specimens && data.specimens.length > 0) {
      loadCollection(currentPeriod.value);
    }

    // Reload collection if new bots were added
    if (data.botSpecimens && data.botSpecimens.length > 0) {
      loadCollection(currentPeriod.value);
    }
  }

  /**
   * Show achievement notification toast
   */
  function showAchievementNotification(achievement: Achievement) {
    achievementNotifications.value.push(achievement);
  }

  /**
   * Remove achievement notification
   */
  function removeAchievementNotification(achievementId: number) {
    const index = achievementNotifications.value.findIndex((a) => a.id === achievementId);
    if (index !== -1) {
      achievementNotifications.value.splice(index, 1);
    }
  }

  /**
   * Disconnect from SSE
   */
  function disconnectSSE() {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
      sseConnected.value = false;
    }
  }

  return {
    // State
    initialized,
    loading,
    error,
    sessionId,
    isNewSession,
    isBot,
    visitorMetadata,
    specimenResults,
    contributionSummary,
    visitUnlockedAchievements,
    currentPeriod,
    collection,
    visitorData,
    sseConnected,
    sseDegraded,
    achievementNotifications,

    // Computed
    totalSpecimens,
    totalAchievements,
    geographySpecimens,
    technologySpecimens,
    temporalSpecimens,
    infrastructureSpecimens,
    digitalVisitorsSpecimens,
    achievements,
    achievementProgress,

    // Actions
    initialize,
    recordVisit,
    loadCollection,
    loadVisitorData,
    changePeriod,
    connectSSE,
    disconnectSSE,
    handleRealtimeUpdate,
    showAchievementNotification,
    removeAchievementNotification,
  };
});
